/**
 * Centralized Error Handling Utilities
 * Prevents site crashes from unhandled errors
 */

import i18n from '@/i18n';
import { notifications } from './notifications';
import { logger } from '@/utils/logger';

const t = (key: string, options?: Record<string, unknown>) => i18n.t(key, options);

// Custom error types
export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly code?: string;
  public readonly details?: unknown;

  constructor(message: string, statusCode: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;
  }
}

export class NetworkError extends Error {
  constructor(message?: string) {
    super(message || i18n.t('errors.networkError'));
    this.name = 'NetworkError';
  }
}

export class ValidationError extends Error {
  public readonly fields?: Record<string, string>;

  constructor(message: string, fields?: Record<string, string>) {
    super(message);
    this.name = 'ValidationError';
    this.fields = fields;
  }
}

/**
 * Safe JSON parsing - prevents crashes from malformed responses
 */
export async function safeJsonParse<T = unknown>(response: Response): Promise<T> {
  const text = await response.text();

  if (!text || text.trim() === '') {
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch (parseError) {
    logger.error('[safeJsonParse] Failed to parse JSON:', {
      status: response.status,
      url: response.url,
      textPreview: text.substring(0, 200),
    });

    // Return a structured error object instead of crashing
    return {
      error: 'invalid_response',
      message: t('errors.invalidResponse'),
      rawText: text.substring(0, 500),
    } as T;
  }
}

/**
 * Handle API response with proper error handling
 */
export async function handleApiResponse<T>(
  response: Response,
  options?: {
    errorMessage?: string;
    showNotification?: boolean;
  }
): Promise<T> {
  const { errorMessage, showNotification = false } = options || {};

  if (!response.ok) {
    const errorBody = await safeJsonParse<{
      error?: string;
      message?: string;
      code?: string;
      details?: unknown;
    }>(response);

    const message = errorBody.message || errorBody.error || errorMessage || t('errors.httpError', { status: response.status });

    if (showNotification) {
      notifications.error(message);
    }

    throw new ApiError(message, response.status, errorBody.code, errorBody.details);
  }

  return safeJsonParse<T>(response);
}

/**
 * Wrap async function with error handling
 */
export function withErrorHandling<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  options?: {
    fallback?: ReturnType<T> extends Promise<infer R> ? R : never;
    showNotification?: boolean;
    logError?: boolean;
    context?: string;
  }
): T {
  const { fallback, showNotification = true, logError = true, context } = options || {};

  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (logError) {
        logger.error(`[${context || 'Error'}]`, error);
      }

      if (showNotification && error instanceof Error) {
        notifications.error(error.message || t('errors.unexpectedError'));
      }

      if (fallback !== undefined) {
        return fallback;
      }

      throw error;
    }
  }) as T;
}

/**
 * Safe fetch wrapper with timeout and error handling
 */
export async function safeFetch(
  url: string,
  options?: RequestInit & { timeout?: number }
): Promise<Response> {
  const { timeout = 30000, ...fetchOptions } = options || {};

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new NetworkError(t('errors.timeout'));
      }
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new NetworkError(t('errors.serverUnreachable'));
      }
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Extract user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof NetworkError) {
    return error.message;
  }

  if (error instanceof ValidationError) {
    return error.message;
  }

  if (error instanceof Error) {
    // Common error messages translation
    const message = error.message.toLowerCase();

    if (message.includes('failed to fetch') || message.includes('networkerror')) {
      return t('errors.networkError');
    }

    if (message.includes('timeout') || message.includes('aborted')) {
      return t('notifications.network.timeout');
    }

    if (message.includes('unauthorized') || message.includes('401')) {
      return t('errors.sessionExpired');
    }

    if (message.includes('forbidden') || message.includes('403')) {
      return t('errors.accessDenied');
    }

    if (message.includes('not found') || message.includes('404')) {
      return t('errors.notFound');
    }

    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return t('errors.unexpectedError');
}

/**
 * Log error with context (for debugging)
 */
export function logError(error: unknown, context?: string): void {
  const timestamp = new Date().toISOString();
  const errorInfo = {
    timestamp,
    context,
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    type: error instanceof Error ? error.constructor.name : typeof error,
  };

  logger.error('[Error Logger]', errorInfo);
}

/**
 * Retry failed operation with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options?: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    shouldRetry?: (error: unknown) => boolean;
  }
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    shouldRetry = (error: unknown) => error instanceof NetworkError,
  } = options || {};

  let lastError: unknown;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }

      logger.warn(`[Retry] Attempt ${attempt + 1}/${maxRetries} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      delay = Math.min(delay * 2, maxDelay);
    }
  }

  throw lastError;
}

export default {
  ApiError,
  NetworkError,
  ValidationError,
  safeJsonParse,
  handleApiResponse,
  withErrorHandling,
  safeFetch,
  getErrorMessage,
  logError,
  retryWithBackoff,
};
